/**
 * DraftClaimLorStep (Legacy)
 *
 * Handles the draft phase of claim filing: confirming final details required
 * for a Letter of Representation (LoR), generating the LoR from templates,
 * previewing documents, and sending via fax with lightweight status UI.
 * Integrates transcript and audio evidence to support field review.
 *
 * This is the legacy version of the LOR drafting interface.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { PDFDocument } from 'pdf-lib';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { DisclosureSection } from '@/components/claims-filing/common/DisclosureSection';
import { ArtifactBrowserPanel } from '@/components/claims-filing/review/ArtifactBrowserPanel';
import { FaxWizard } from '@/components/fax/wizard/FaxWizard';
import SendFrontEmailDialog from '@/components/matter/SendFrontEmailDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
//
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api';
import { useGetAudioConversationsQuery } from '@/services/api/audioConversationService';
import { useGetBlandCallByIdQuery } from '@/services/api/blandCallService';
import { useGetBlobsQuery } from '@/services/api/blobService';
import {
  useGetCoverageQuery,
  useGetCoveragesQuery,
  useLorDataUpdatedMutation,
  usePromoteLorSelectionsMutation,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import type { ReadTemplateField } from '@/services/api/generated/templatesV2Service';
import {
  useTemplateFieldsQuery,
} from '@/services/api/generated/templatesV2Service';
import {
  useCreateDocumentMutation,
} from '@/services/api/lettersService';
import { useGetMatterQuery } from '@/services/api/mattersService';
//
import type { Task, TaskStatus } from '@/services/types/client-intake-types';
// import { useGetTranscriptSegmentsQuery } from '@/services/api/transcriptService';
import type {
  AdjusterDetail,
  ContactHandleSchema,
  CoverageSchema,
} from '@/services/types/coverage-types';
import type {
  LorDisplayField,
  LoRScalarChoice,
} from '@/services/types/letters-types';
import {
  buildCoverageChoiceMap,
  buildLorDisplayFields,
  DocumentFieldMapping,
} from '@/services/types/letters-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';
import { extractContactChannels } from '@/services/utils/coverageHandles';

import { FinalizeDetails } from '../hooks/useClaimFilingState';
import { getLatestCoverageTaskByCanonicalType } from '../premises/premisesFlow';
import type { TemplateLite } from '../types';
import { ArchivedTemplateWarning } from './ArchivedTemplateWarning';
//
import { FinalizeDetailsPanel } from './FinalizeDetailsPanel';
import { useArchivedTemplateCheck } from './hooks/useArchivedTemplateCheck';
import { useLorDocuments, useLorTemplates } from './hooks/useLorData';
import { useLorPreview } from './hooks/usePreview';
import { LorList } from './LorList';
import { LorPreviewDialog } from './LorPreviewDialog';
import { LorTemplateSelector } from './LorTemplateSelector';
import { RequiredLorFieldsPanel } from './RequiredLorFieldsPanel';
import { StackedTranscriptPane } from './StackedTranscriptPane';

/** Props for `DraftClaimLorStep`. */
interface DraftClaimLorStepProps {
  /** Matter id (used for templates, documents, and transcripts). */
  matterId: string;
  /** Coverage id for template selection and merge fields. */
  coverageId: string;
  /** Optional bland call id to scope transcript selection. */
  blandCallId?: string;
  /** Setter to control the focused field key (used on hover). */
  setActiveFieldKey: (key: string | null) => void;
  /** Final details used to generate the LoR. */
  finalizeDetails: FinalizeDetails;
  /** Update a specific finalize detail field. */
  updateFinalizeDetail: <K extends keyof FinalizeDetails>(
    key: K,
    value: FinalizeDetails[K],
  ) => void;
  /** Indicates whether local edits need saving. */
  hasUnsavedFinalizeChanges: boolean;
  /** Persist finalize details to backend. */
  saveFinalizeDetails: () => void;
  /** Client-side map of LoR id to transmission id for tracking. */
  activeFaxTransmissions?: Map<string, string>;
  /** Initiate a fax send for the given LoR id. */
  onSendFax: (lorId: string, faxNumber: string) => void;
  /** Callback when a client-tracked transmission reaches completion. */
  onTransmissionComplete?: (lorId: string) => void;
  /** Global sending flag to disable buttons when firing. */
  isSendingFax?: boolean;
}

type LorFieldValue = string | LoRScalarChoice;

export const DraftClaimLorStep: React.FC<DraftClaimLorStepProps> = ({
  matterId,
  coverageId,
  blandCallId,
  setActiveFieldKey,
  finalizeDetails,
  updateFinalizeDetail,
  hasUnsavedFinalizeChanges,
  saveFinalizeDetails,
  activeFaxTransmissions = new Map<string, string>(),
  onSendFax,
  onTransmissionComplete,
  isSendingFax = false,
}) => {
  // Constants moved to shared constants.ts

  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [isPreviewRequested, setIsPreviewRequested] = useState<boolean>(false);
  const [isFaxWizardOpen, setIsFaxWizardOpen] = useState<boolean>(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState<boolean>(false);
  const [isRequiredFieldsOpen, setIsRequiredFieldsOpen] =
    useState<boolean>(true);
  const [isReviewOutputOpen, setIsReviewOutputOpen] = useState<boolean>(true);
  const [selectedAppendBlobId, setSelectedAppendBlobId] = useState<
    string | null
  >(null);
  // Task completion wiring (reuse StepActionHeader logic)
  const { data: tasksResponse } = useGetTasksQuery(
    {
      matter: matterId,
      limit: 200,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const [updateTask] = useUpdateTaskMutation();
  const { data: taskStatuses = [] as TaskStatus[] } = useGetTaskStatusesQuery();
  const sendLorTask = useMemo(() => {
    const tasks = (tasksResponse?.results || []) as Task[];
    return getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageId,
      CanonicalTaskType.SEND_LOR,
    );
  }, [tasksResponse, coverageId]);

  const markSendLorTaskCompleted = async () => {
    if (!sendLorTask) return;
    try {
      const completedStatus = (taskStatuses as TaskStatus[]).find(
        (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
      );
      await updateTask({
        matterId,
        taskId: sendLorTask.id,
        task: {
          status_id: completedStatus?.id,
          completed_at: new Date().toISOString(),
        },
      }).unwrap();
    } catch (e) {
      // eslint-disable-next-line no-console
      // eslint-disable-next-line no-console
      console.error('Failed to mark Send LoR task completed', e);
      toast.error('Could not mark Send LoR task completed');
    }
  };

  // Load coverages for the matter to display in LOR list
  const { data: coveragesData } = useGetCoveragesQuery({ matterId });
  const coveragesMap = useMemo(() => {
    const map = new Map<string, CoverageSchema>();
    coveragesData?.forEach((cov) => {
      map.set(cov.id, cov);
    });
    return map;
  }, [coveragesData]);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [evidenceByKey, setEvidenceByKey] = useState<Record<string, boolean>>({
    'final:policy': false,
    'final:claim': false,
    'final:adjName': false,
    'final:adjEmail': false,
    'final:adjFax': false,
  });

  // Transcripts integration – scope to bland call when available
  const { data: conversations = [], isLoading: isConvosLoading } =
    useGetAudioConversationsQuery(matterId);

  useGetBlandCallByIdQuery(blandCallId || skipToken);

  // Conversation selection logic is handled inside StackedTranscriptPane

  const hasBlandConversations = useMemo(() => {
    if (!Array.isArray(conversations)) return false;
    return conversations.some((c) =>
      String(c?.source || '')
        .toLowerCase()
        .includes('bland'),
    );
  }, [conversations]);

  // const { data: convoDetail } = useGetAudioConversationDetailQuery(
  //   conversationId ? { matterId: _matterId, conversationId } : skipToken,
  // );

  // const { data: segments = [] } = useGetTranscriptSegmentsQuery(
  //   conversationId ? { matterId: _matterId, conversationId } : skipToken,
  // );

  // Real LoR list: fetch matter documents and filter by LoR template types
  const {
    lorDocs,
    isLoading: isDocsLoading,
    error: docsError,
    refetch: refetchDocs,
  } = useLorDocuments(matterId);

  // Filter lorDocs to only those belonging to (or unlinked from) this coverage
  const lorDocsForCoverage = useMemo(() => {
    return lorDocs.filter(
      (doc) => !doc.coverage_id || doc.coverage_id === coverageId,
    );
  }, [lorDocs, coverageId]);

  // Check if the template used by the current LOR document has been archived
  const {
    isTemplateArchived,
    templateStatus,
    templateName: archivedTemplateName,
    isLoading: isArchiveCheckLoading,
    isError: isArchiveCheckError,
  } = useArchivedTemplateCheck(lorDocsForCoverage);

  // Fetch all blobs for this matter; we'll offer PDFs as append options
  const { data: blobs = [] } = useGetBlobsQuery(matterId);
  const appendPdfOptions: Array<{ id: string; label: string }> = useMemo(() => {
    return (blobs || [])
      .filter((b) => {
        const mime = (b.file_mime_type || '').toLowerCase();
        const ext = (b.file_extension || '').toLowerCase();
        return mime === 'application/pdf' || ext === 'pdf';
      })
      .map((b) => ({ id: b.id, label: b.name || 'Attachment.pdf' }));
  }, [blobs]);

  // Fetch matter (for firmId) and coverage (for template decision)
  const { data: coverage } = useGetCoverageQuery({
    matterId,
    coverageId,
  });
  const [updateCoverage] = useUpdateCoverageMutation();
  const [lorDataUpdated] = useLorDataUpdatedMutation();
  const [promoteLorSelections] = usePromoteLorSelectionsMutation();

  // Templates for firm
  const {
    lorTemplates,
    isLoading: isTemplatesLoading,
    error: templatesError,
    pickDefaultTemplateId,
    representingAttorneyId,
    representingAttorneyName,
  } = useLorTemplates(matterId);

  // Filter available templates by coverage category (1P vs 3P vs 1P Health)
  const coverageFilteredTemplates = useMemo(() => {
    const cat =
      (coverage as { category?: string | null } | null)?.category ?? null;
    if (cat === 'FIRST_PARTY') {
      return lorTemplates.filter(
        (t) => t.category === 'LOR_FIRST_PARTY',
      );
    }
    if (cat === 'FIRST_PARTY_HEALTH') {
      return lorTemplates.filter(
        (t) => t.category === 'LOR_HEALTH',
      );
    }
    if (cat === 'THIRD_PARTY') {
      return lorTemplates.filter(
        (t) => t.category === 'LOR_THIRD_PARTY',
      );
    }
    return lorTemplates;
  }, [coverage, lorTemplates]);

  const {
    displayTemplates,
    attorneyFilterActive,
  }: {
    displayTemplates: TemplateLite[];
    attorneyFilterActive: boolean;
  } = useMemo(() => {
    if (!representingAttorneyId) {
      return {
        displayTemplates: coverageFilteredTemplates,
        attorneyFilterActive: false,
      };
    }

    // Include templates that match the representing attorney OR have no
    // attorney assigned.  Previously templates without an attorney were
    // excluded when at least one attorney-specific template existed, which
    // unintentionally hid generic firm-wide templates.
    const relevantTemplates = coverageFilteredTemplates.filter(
      (t) => !t.attorney_id || t.attorney_id === representingAttorneyId,
    );

    if (relevantTemplates.length > 0) {
      return {
        displayTemplates: relevantTemplates,
        attorneyFilterActive: true,
      };
    }

    return {
      displayTemplates: coverageFilteredTemplates,
      attorneyFilterActive: false,
    };
  }, [coverageFilteredTemplates, representingAttorneyId]);

  // Default selection respecting coverage + attorney filters
  useEffect(() => {
    if (!coverage) return;
    if (!displayTemplates.length) {
      setSelectedTemplateId(null);
      return;
    }
    const cat =
      (coverage as { category?: string | null } | null)?.category ?? null;
    setSelectedTemplateId((current) => {
      if (current && displayTemplates.some((t) => t.id === current)) {
        return current;
      }
      const fallback = pickDefaultTemplateId(cat, displayTemplates);
      return fallback ?? displayTemplates[0]?.id ?? null;
    });
  }, [coverage, displayTemplates, pickDefaultTemplateId]);

  // Fetch firm id and template fields for selected template
  const { data: matterForFirm } = useGetMatterQuery(matterId);
  const firmIdForFields = matterForFirm?.firm?.id || null;
  const { data: rawTemplateFields, isLoading: isTemplateFieldsLoading } =
    useTemplateFieldsQuery(
      selectedTemplateId
        ? { id: selectedTemplateId, matterId, coverageId }
        : (skipToken as unknown as { id: string }),
      {
        refetchOnMountOrArgChange: true,
      },
    );

  // Build LorDisplayField[] from v2 template fields (no legacy lor-context merge).
  // Build choice arrays from coverage data for dropdown fields
  const choiceMap = useMemo(
    () => buildCoverageChoiceMap(coverage, coverageId),
    [coverage, coverageId],
  );

  const templateFields = useMemo(() => {
    if (!rawTemplateFields) return undefined;
    const v2Fields = rawTemplateFields as unknown as ReadTemplateField[];
    return buildLorDisplayFields(v2Fields, choiceMap);
  }, [rawTemplateFields, choiceMap]);

  // Local edits for required LoR fields
  const [lorFieldEdits, setLorFieldEdits] = useState<
    Record<string, LorFieldValue>
  >({});

  const requiredFieldValues: Record<string, LorFieldValue> = useMemo(() => {
    const result: Record<string, LorFieldValue> = {};
    const fields = templateFields?.fields || [];
    const getFieldKey = (tf: LorDisplayField): string => {
      const ref = (tf.reference || '').trim();
      if (ref) return ref;
      return tf.id;
    };
    fields.forEach((f) => {
      const key = getFieldKey(f);

      // 1) User edits win ONLY if they are non-empty/meaningful
      const edited = lorFieldEdits[key] as unknown;
      const editedIsNonEmptyString =
        typeof edited === 'string' && edited.trim() !== '';
      const editedIsEmptyString =
        typeof edited === 'string' && edited.trim() === '';
      const editedIsChoiceWithValue =
        !!edited &&
        typeof edited === 'object' &&
        !Array.isArray(edited) &&
        'value' in (edited as Record<string, unknown>) &&
        String((edited as Record<string, unknown>).value ?? '').trim() !== '';
      if (
        editedIsNonEmptyString ||
        editedIsEmptyString ||
        editedIsChoiceWithValue
      ) {
        result[key] = edited as LorFieldValue;
        return;
      }

      // 2) CARRIER FIELD DICTIONARY MAPPING - High priority for carrier fields
      const rawMapping = f.reference;
      const mappingForCarrier = String((rawMapping || '').trim());
      const fieldMapping = mappingForCarrier.toLowerCase();

      // Extract carrier data based on actual handle structure
      const carrierName = coverage?.carrier?.name || '';
      const handles = coverage?.carrier?.handles || [];

      // Find specific handle types
      const emailHandle = handles.find((h) => h.handle_type === 'EMAIL');
      const phoneHandle = handles.find((h) => h.handle_type === 'PHONE');
      const faxHandle = handles.find((h) => h.handle_type === 'FAX');

      // Type for address handle with potential address fields
      type AddressHandle = ContactHandleSchema & {
        address_line1?: string;
        address_line2?: string;
      };

      const addressHandle = handles.find(
        (h) =>
          h.handle_type === 'ADDRESS' ||
          h.handle_type === 'MAILING_ADDRESS' ||
          (h as AddressHandle).address_line1 ||
          (h as AddressHandle).address_line2,
      ) as AddressHandle | undefined;

      const carrierEmail = emailHandle?.value || '';
      const carrierPhone = phoneHandle?.value || '';
      const carrierFax = faxHandle?.value || '';

      // Extract address lines from carrier object or handles
      let carrierAddress1 = coverage?.carrier?.address || '';
      let carrierAddress2 = '';

      // If we have an address handle, use its structured address data
      if (addressHandle) {
        carrierAddress1 =
          addressHandle.address_line1 || addressHandle.value || carrierAddress1;
        carrierAddress2 = addressHandle.address_line2 || '';
      }

      // Apply fixed mappings using dictionary
      const carrierFieldMappings: Record<string, string> = {
        carrier_name: carrierName,
        carrier_address_1: carrierAddress1,
        carrier_address_2: carrierAddress2,
        carrier_email: carrierEmail,
        carrier_phone: carrierPhone,
        carrier_fax: carrierFax,
      };

      if (fieldMapping in carrierFieldMappings) {
        const mappedValue = carrierFieldMappings[fieldMapping];
        // Only use carrier field mapping if we have a non-empty value
        // This allows empty fields to fall through to backend defaults
        if (mappedValue) {
          result[key] = mappedValue as LorFieldValue;
          return;
        }
      }

      // 3) Prefer extracted finalize defaults (from "Review Call Output")
      const finalizeMappingRaw = f.reference;
      const finalizeMapping = String((finalizeMappingRaw || '').trim());
      const finalizeVal = (() => {
        switch (finalizeMapping as DocumentFieldMapping) {
          case DocumentFieldMapping.POLICY_NUMBER:
            return finalizeDetails.policyNumber?.trim() || undefined;
          case DocumentFieldMapping.CLAIM_NUMBER:
            return finalizeDetails.claimNumber?.trim() || undefined;
          case DocumentFieldMapping.ADJUSTER_NAME:
            return finalizeDetails.adjusterName?.trim() || undefined;
          case DocumentFieldMapping.ADJUSTER_CONTACT:
            if (finalizeDetails.adjusterContactChoice) {
              return finalizeDetails.adjusterContactChoice as unknown as LorFieldValue;
            }
            return (
              finalizeDetails.adjusterFax?.trim() ||
              finalizeDetails.adjusterEmail?.trim() ||
              finalizeDetails.adjusterPhone?.trim() ||
              undefined
            );
          default:
            return undefined;
        }
      })();
      const hasFinalize =
        finalizeVal !== undefined &&
        finalizeVal !== null &&
        (typeof finalizeVal !== 'string' ||
          (finalizeVal as string).trim() !== '') &&
        (!Array.isArray(finalizeVal) ||
          (Array.isArray(finalizeVal) && finalizeVal.length > 0));
      if (hasFinalize) {
        result[key] = finalizeVal as LorFieldValue;
        return;
      }

      // 4) Prefer backend template defaults (e.g., LLM-split address)
      // If default_value is an array of choices, auto-select the first choice
      const backend = f.default_value as unknown;
      if (Array.isArray(backend) && backend.length > 0) {
        // Check if it's an array of scalar choices (suggestions)
        const firstChoice = backend[0];
        if (
          firstChoice &&
          typeof firstChoice === 'object' &&
          'value' in (firstChoice as Record<string, unknown>)
        ) {
          // Auto-select the first choice from the suggestions
          result[key] = firstChoice as LorFieldValue;
          return;
        }
      }
      const hasBackend =
        backend !== null &&
        backend !== undefined &&
        !Array.isArray(backend) &&
        (typeof backend !== 'string' || (backend as string).trim() !== '');

      if (hasBackend) {
        result[key] = backend as LorFieldValue;
        return;
      }

      // 5) Otherwise blank - no mapping found
      result[key] = '';
    });

    // Carrier data summary (debug logs removed for production)

    return result;
  }, [
    templateFields,
    lorFieldEdits,
    finalizeDetails,
    coverage?.carrier?.name,
    coverage?.carrier?.address,
    coverage?.carrier?.handles,
  ]);

  // Contact method options are computed after resolving fallback handles

  // Fallbacks from coverage/matter defaults (display-only unless user updates)
  const coerceFieldValue = (val: unknown): LorFieldValue | undefined => {
    if (val === null || val === undefined) return undefined;
    if (typeof val === 'string') return val;
    if (
      typeof val === 'object' &&
      !Array.isArray(val) &&
      Object.prototype.hasOwnProperty.call(
        val as Record<string, unknown>,
        'value',
      )
    ) {
      return val as LoRScalarChoice;
    }
    return undefined;
  };

  const toDisplayString = (value: LorFieldValue | undefined): string => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') return value.value ?? '';
    return '';
  };

  const isScalarChoiceValue = (value: unknown): value is LoRScalarChoice =>
    Boolean(
      value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        'value' in (value as Record<string, unknown>),
    );

  // Once defaultValues are available, backfill any undefined field values
  useEffect(() => {
    const fields = templateFields?.fields || [];
    if (!fields.length) return;
    setLorFieldEdits((prev) => {
      const next = { ...prev };
      fields.forEach((f) => {
        const key = (f.reference || '').trim() || f.id;
        if (next[key] === undefined) {
          // Prefer backend default_value; then finalize-derived defaults; then FE defaults
          const hasBackendDefault =
            f.default_value !== null &&
            f.default_value !== undefined &&
            (typeof f.default_value !== 'string' ||
              (typeof f.default_value === 'string' &&
                f.default_value.trim() !== ''));

          let valueToUse: unknown;
          if (hasBackendDefault) {
            valueToUse = f.default_value;
          } else {
            let finVal: unknown;
            switch (key as DocumentFieldMapping) {
              case DocumentFieldMapping.POLICY_NUMBER:
                finVal = finalizeDetails.policyNumber?.trim() || undefined;
                break;
              case DocumentFieldMapping.CLAIM_NUMBER:
                finVal = finalizeDetails.claimNumber?.trim() || undefined;
                break;
              case DocumentFieldMapping.ADJUSTER_NAME:
                finVal = finalizeDetails.adjusterName?.trim() || undefined;
                break;
              case DocumentFieldMapping.ADJUSTER_CONTACT:
                finVal =
                  (finalizeDetails.adjusterContactChoice as unknown as LorFieldValue) ||
                  finalizeDetails.adjusterFax?.trim() ||
                  finalizeDetails.adjusterEmail?.trim() ||
                  finalizeDetails.adjusterPhone?.trim() ||
                  undefined;
                break;
              default:
                finVal = undefined;
            }
            if (
              finVal !== undefined &&
              finVal !== null &&
              (typeof finVal !== 'string' || (finVal as string).trim() !== '')
            ) {
              valueToUse = finVal;
            }
          }

          const coerced = coerceFieldValue(valueToUse);
          if (coerced !== undefined) next[key] = coerced;
        }
      });
      return next;
    });
  }, [templateFields, finalizeDetails]);

  // Derive fallback values directly from coverage data (no lor-context endpoint).
  const primaryAdjuster = Array.isArray(coverage?.adjusters)
    ? ((coverage?.adjusters as AdjusterDetail[])[0] as AdjusterDetail)
    : undefined;

  const fallbackPolicy = String(
    (coverage as CoverageSchema | undefined)?.policy_identifiers?.[0]
      ?.identifier_value ?? '',
  );
  const fallbackClaim = String(
    (coverage as CoverageSchema | undefined)?.claims?.[0]?.claim_identifier ??
      '',
  );
  const fallbackAdjName = primaryAdjuster?.name ?? '';
  const adjHandles = Array.isArray(primaryAdjuster?.handles) ? primaryAdjuster.handles : [];
  const { email: fallbackEmail, phone: fallbackPhone, fax: fallbackFax } =
    extractContactChannels(adjHandles as ContactHandleSchema[]);

  // Carrier handles fallback for email/phone/fax (preferred for LoR and follow-ups)
  const carrierHandles = Array.isArray(coverage?.carrier?.handles) ? coverage.carrier.handles : [];
  const {
    email: fallbackCarrierEmail,
    phone: fallbackCarrierPhone,
    fax: fallbackCarrierFax,
  } = extractContactChannels(carrierHandles as ContactHandleSchema[]);

  // Now that fallbackEmail/fax are known, compute Contact Method options and selection
  const contactMethodOptions = useMemo(() => {
    const opts = new Set<string>();
    if (finalizeDetails.adjusterFax || fallbackFax) opts.add('FAX');
    if (finalizeDetails.adjusterEmail || fallbackEmail) opts.add('EMAIL');
    const rank = (method: string) => {
      if (method === 'FAX') return 0;
      if (method === 'EMAIL') return 1;
      return 2;
    };
    return Array.from(opts).sort((a, b) => rank(a) - rank(b));
  }, [
    finalizeDetails.adjusterFax,
    finalizeDetails.adjusterEmail,
    fallbackFax,
    fallbackEmail,
  ]);

  // Keep default contact method in state where needed; value derived on demand elsewhere

  useEffect(() => {
    setLorFieldEdits((prev) => {
      const current = prev[DocumentFieldMapping.CONTACT_METHOD];
      const currentStr = typeof current === 'string' ? current : undefined;
      let nextVal: string | undefined = currentStr;
      if (!currentStr || !contactMethodOptions.includes(currentStr)) {
        if (contactMethodOptions.includes('FAX')) nextVal = 'FAX';
        else if (contactMethodOptions.includes('EMAIL')) nextVal = 'EMAIL';
        else nextVal = undefined;
      }
      if (nextVal === currentStr) return prev;
      const next = { ...prev } as Record<string, LorFieldValue>;
      if (nextVal) next[DocumentFieldMapping.CONTACT_METHOD] = nextVal;
      else delete next[DocumentFieldMapping.CONTACT_METHOD];
      return next;
    });
  }, [contactMethodOptions]);

  // Build merge fields
  const buildMergeFields = (): Record<string, string> => {
    const merged: Record<string, string> = {};

    // Seed merge fields from resolved requiredFieldValues (already includes
    // backend defaults, finalize details, and user edits).
    Object.entries(requiredFieldValues).forEach(([k, v]) => {
      if (typeof v === 'string' && v.trim() !== '') merged[k] = v;
      if (typeof v === 'object' && v?.value) merged[k] = String(v.value);
    });

    // Apply edits from required fields panel
    let contactChoice: LoRScalarChoice | null = null;
    let manualContactValue = '';
    let contactMethodOverride: string | undefined;

    Object.entries(lorFieldEdits).forEach(([k, v]) => {
      if (isScalarChoiceValue(v)) {
        merged[k] = v.value;
        if (k === DocumentFieldMapping.ADJUSTER_CONTACT) {
          contactChoice = v;
        }
      } else if (typeof v === 'string') {
        const trimmed = v.trim();
        // Respect explicit empty values to override defaults
        if (trimmed.length === 0) {
          merged[k] = '';
          if (k === DocumentFieldMapping.ADJUSTER_CONTACT) {
            manualContactValue = '';
          }
        } else {
          merged[k] = trimmed;
          if (k === DocumentFieldMapping.ADJUSTER_CONTACT) {
            manualContactValue = trimmed;
          }
        }
      }

      if (k === DocumentFieldMapping.CONTACT_METHOD && typeof v === 'string') {
        const method = v.trim().toUpperCase();
        if (method) contactMethodOverride = method;
      }
    });

    const hasExplicitEdit = (key: string): boolean =>
      Object.prototype.hasOwnProperty.call(lorFieldEdits, key);

    if (
      finalizeDetails.policyNumber &&
      !hasExplicitEdit(DocumentFieldMapping.POLICY_NUMBER)
    ) {
      merged[DocumentFieldMapping.POLICY_NUMBER] = finalizeDetails.policyNumber;
    }
    if (
      finalizeDetails.claimNumber &&
      !hasExplicitEdit(DocumentFieldMapping.CLAIM_NUMBER)
    ) {
      merged[DocumentFieldMapping.CLAIM_NUMBER] = finalizeDetails.claimNumber;
    }
    if (
      finalizeDetails.adjusterName &&
      !hasExplicitEdit(DocumentFieldMapping.ADJUSTER_NAME)
    ) {
      merged[DocumentFieldMapping.ADJUSTER_NAME] = finalizeDetails.adjusterName;
    }

    if (
      !contactChoice &&
      isScalarChoiceValue(finalizeDetails.adjusterContactChoice)
    ) {
      contactChoice = finalizeDetails.adjusterContactChoice;
    }

    const manualContact = manualContactValue;

    if (contactChoice) {
      merged[DocumentFieldMapping.ADJUSTER_CONTACT] = contactChoice.value;
      const choiceHandleType =
        contactChoice.context?.contact_handle?.handle_type;
      const normalizedMethod = (contactMethodOverride || choiceHandleType)
        ?.toString()
        .toUpperCase();
      if (normalizedMethod) {
        merged[DocumentFieldMapping.CONTACT_METHOD] = normalizedMethod;
      }
    } else {
      let fallbackContact = '';
      if (manualContact) fallbackContact = manualContact;
      else if (finalizeDetails.adjusterFax)
        fallbackContact = finalizeDetails.adjusterFax;
      else if (finalizeDetails.adjusterEmail)
        fallbackContact = finalizeDetails.adjusterEmail;
      else if (finalizeDetails.adjusterPhone)
        fallbackContact = finalizeDetails.adjusterPhone;

      if (fallbackContact) {
        merged[DocumentFieldMapping.ADJUSTER_CONTACT] = fallbackContact;
        let inferredMethod: string | undefined = contactMethodOverride;
        if (!inferredMethod) {
          if (
            fallbackContact === finalizeDetails.adjusterFax &&
            finalizeDetails.adjusterFax
          ) {
            inferredMethod = 'FAX';
          } else if (
            fallbackContact === finalizeDetails.adjusterEmail &&
            finalizeDetails.adjusterEmail
          ) {
            inferredMethod = 'EMAIL';
          } else if (
            fallbackContact === finalizeDetails.adjusterPhone &&
            finalizeDetails.adjusterPhone
          ) {
            inferredMethod = 'PHONE';
          }
        }

        if (inferredMethod) {
          merged[DocumentFieldMapping.CONTACT_METHOD] = inferredMethod;
        }
      } else if (contactMethodOverride) {
        merged[DocumentFieldMapping.CONTACT_METHOD] = contactMethodOverride;
      }
    }

    // Ensure carrier phone/fax are wired from coverage handles when available
    // and not explicitly overridden by user edits or prior defaults.
    if (
      !hasExplicitEdit(DocumentFieldMapping.CARRIER_TELEPHONE) &&
      !merged[DocumentFieldMapping.CARRIER_TELEPHONE] &&
      fallbackCarrierPhone
    ) {
      merged[DocumentFieldMapping.CARRIER_TELEPHONE] = fallbackCarrierPhone;
    }
    if (
      !hasExplicitEdit(DocumentFieldMapping.CARRIER_FAX) &&
      !merged[DocumentFieldMapping.CARRIER_FAX] &&
      fallbackCarrierFax
    ) {
      merged[DocumentFieldMapping.CARRIER_FAX] = fallbackCarrierFax;
    }

    // Add UPPER_CASE aliases for backend compatibility
    const fieldsForTemplate = (templateFields?.fields ||
      []) as LorDisplayField[];
    fieldsForTemplate.forEach((tf) => {
      const key = (tf.reference || '').trim() || tf.id;
      if (!key) return;

      const val = merged[key];
      if (!val) return;

      const upperAlias = key.toUpperCase();
      if (!merged[upperAlias]) {
        merged[upperAlias] = val;
      }
    });

    return merged;
  };

  const [createDocument, { isLoading: isCreating }] =
    useCreateDocumentMutation();

  const handlePreviewOpenChange = (open: boolean) => {
    if (!open) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setPreviewDocId(null);
      setIsPreviewRequested(false);
    }
    setPreviewOpen(open);
  };

  const handleViewLor = (id: string) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewDocId(id);
    setIsPreviewRequested(true);
    setPreviewOpen(true);
  };

  const handleDownloadDocx = () => {
    if (!previewDocId) return;
    const url = `${backendHost}/api/matters/${matterId}/documents/${previewDocId}/download/`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadPdf = async () => {
    if (!previewDocId) return;
    // Reuse preview URL for PDF; if not available yet, trigger preview
    if (!previewUrl) {
      setIsPreviewRequested(true);
      requestPreview();
      return;
    }
    try {
      // If no append selection, do a direct download of the LoR preview
      if (!selectedAppendBlobId) {
        const link = document.createElement('a');
        link.href = previewUrl;
        link.download = previewFilename || 'Letter-of-Representation.pdf';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // Find the selected blob and fetch both PDFs
      const selected = (blobs || []).find((b) => b.id === selectedAppendBlobId);
      if (!selected || !selected.download_url) {
        // Fallback to direct download if the selected blob is unavailable
        const link = document.createElement('a');
        link.href = previewUrl;
        link.download = previewFilename || 'Letter-of-Representation.pdf';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      const [lorResp, appendResp] = await Promise.all([
        fetch(previewUrl),
        fetch(selected.download_url),
      ]);
      if (!lorResp.ok) throw new Error('Failed to fetch LoR PDF');
      if (!appendResp.ok) throw new Error('Failed to fetch selected PDF');

      const [lorBlob, appendBlob] = await Promise.all([
        lorResp.blob(),
        appendResp.blob(),
      ]);

      const lorBytes = await lorBlob.arrayBuffer();
      const appendBytes = await appendBlob.arrayBuffer();

      const lorPdf = await PDFDocument.load(lorBytes, {
        ignoreEncryption: true,
      });
      const appendPdf = await PDFDocument.load(appendBytes, {
        ignoreEncryption: true,
      });
      const out = await PDFDocument.create();
      const lorPages = await out.copyPages(lorPdf, lorPdf.getPageIndices());
      lorPages.forEach((p) => out.addPage(p));
      const appendPages = await out.copyPages(
        appendPdf,
        appendPdf.getPageIndices(),
      );
      appendPages.forEach((p) => out.addPage(p));
      const outBytes = await out.save();
      // Normalize to ArrayBuffer to satisfy stricter BlobPart typings
      const outBuffer = new ArrayBuffer(outBytes.byteLength);
      new Uint8Array(outBuffer).set(outBytes);
      const mergedBlob = new Blob([outBuffer], { type: 'application/pdf' });
      const mergedUrl = URL.createObjectURL(mergedBlob);
      const a = document.createElement('a');
      a.href = mergedUrl;
      a.download = previewFilename || 'Letter-of-Representation.pdf';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(mergedUrl);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to download merged PDF', err);
      toast.error('Failed to download merged PDF');
    }
  };

  const buildDefaultEmailTo = (): string[] => {
    // Prefer carrier email handle for LoR emails; fall back to adjuster
    if (fallbackCarrierEmail && fallbackCarrierEmail.trim()) {
      return [fallbackCarrierEmail.trim()];
    }
    if (finalizeDetails.adjusterEmail && finalizeDetails.adjusterEmail.trim()) {
      return [finalizeDetails.adjusterEmail.trim()];
    }
    if (fallbackEmail && fallbackEmail.trim()) return [fallbackEmail.trim()];
    return [];
  };

  const buildDefaultEmailSubject = (): string => {
    const carrierName = coverage?.carrier?.name || '';
    const claimNo = finalizeDetails.claimNumber || fallbackClaim;
    const suffix = [carrierName, claimNo].filter(Boolean).join(' • ');
    return suffix
      ? `Letter of Representation — ${suffix}`
      : 'Letter of Representation';
  };

  // Helpers to persist per-field edits back to Coverage using promotion logic.
  // The backend promotes values to position 0 without creating duplicates.
  const persistPolicyNumber = async (next: string) => {
    try {
      await promoteLorSelections({
        matterId,
        coverageId,
        policyNumber: next,
      }).unwrap();
      saveFinalizeDetails();
      // Emit audit event
      try {
        await lorDataUpdated({
          matterId,
          coverageId,
          body: {
            document_id: previewDocId || null,
            mapping: DocumentFieldMapping.POLICY_NUMBER,
            old_value: undefined,
            new_value: next,
          },
        }).unwrap();
      } catch (err) {
        // Intentionally ignore audit emission failures
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to persist policy number', e);
      toast.error('Failed to save policy number');
    }
  };

  const persistClaimNumber = async (next: string) => {
    try {
      await promoteLorSelections({
        matterId,
        coverageId,
        claimNumber: next,
      }).unwrap();
      saveFinalizeDetails();
      // Emit audit event
      try {
        await lorDataUpdated({
          matterId,
          coverageId,
          body: {
            document_id: previewDocId || null,
            mapping: DocumentFieldMapping.CLAIM_NUMBER,
            old_value: undefined,
            new_value: next,
          },
        }).unwrap();
      } catch (err) {
        // Intentionally ignore audit emission failures
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to persist claim number', e);
      toast.error('Failed to save claim number');
    }
  };

  /**
   * Resolve adjuster field values from finalize details with optional overrides.
   * Used to determine what values to send to the promotion endpoint.
   */
  const resolveAdjusterFields = (
    override?: Partial<typeof finalizeDetails>,
  ) => {
    const resolveField = <K extends keyof FinalizeDetails>(
      key: K,
      fallbackValue: FinalizeDetails[K],
    ): FinalizeDetails[K] => {
      if (override && Object.prototype.hasOwnProperty.call(override, key)) {
        const overrideValue = override[key];
        return (
          overrideValue === undefined ? finalizeDetails[key] : overrideValue
        ) as FinalizeDetails[K];
      }
      return finalizeDetails[key] ?? fallbackValue;
    };

    const resolvedName =
      resolveField('adjusterName', '') || fallbackAdjName || null;
    const resolvedEmail =
      resolveField('adjusterEmail', '') || fallbackEmail || '';
    const resolvedPhone =
      resolveField('adjusterPhone', '') || fallbackPhone || '';
    const resolvedFax = resolveField('adjusterFax', '') || fallbackFax || '';

    return {
      name: resolvedName || undefined,
      email: resolvedEmail || undefined,
      phone: resolvedPhone || undefined,
      fax: resolvedFax || undefined,
    };
  };

  const persistAdjuster = async (
    override?: Partial<typeof finalizeDetails>,
  ) => {
    try {
      const resolved = resolveAdjusterFields(override);
      await promoteLorSelections({
        matterId,
        coverageId,
        adjusterName: resolved.name,
        adjusterEmail: resolved.email,
        adjusterPhone: resolved.phone,
        adjusterFax: resolved.fax,
      }).unwrap();
      saveFinalizeDetails();
      // Emit audit event(s) for the specific override keys saved
      try {
        if (override?.adjusterName !== undefined) {
          await lorDataUpdated({
            matterId,
            coverageId,
            body: {
              document_id: previewDocId || null,
              mapping: DocumentFieldMapping.ADJUSTER_NAME,
              old_value: undefined,
              new_value: override.adjusterName || '',
            },
          }).unwrap();
        }
        if (override?.adjusterEmail !== undefined) {
          await lorDataUpdated({
            matterId,
            coverageId,
            body: {
              document_id: previewDocId || null,
              mapping: 'ADJUSTER_EMAIL',
              old_value: undefined,
              new_value: override.adjusterEmail || '',
            },
          }).unwrap();
        }
        if (override?.adjusterFax !== undefined) {
          await lorDataUpdated({
            matterId,
            coverageId,
            body: {
              document_id: previewDocId || null,
              mapping: 'ADJUSTER_FAX',
              old_value: undefined,
              new_value: override.adjusterFax || '',
            },
          }).unwrap();
        }
        if (override?.adjusterPhone !== undefined) {
          await lorDataUpdated({
            matterId,
            coverageId,
            body: {
              document_id: previewDocId || null,
              mapping: 'ADJUSTER_PHONE',
              old_value: undefined,
              new_value: override.adjusterPhone || '',
            },
          }).unwrap();
        }
      } catch (err) {
        // Intentionally ignore audit emission failures
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      // eslint-disable-next-line no-console
      console.error('Failed to persist adjuster details', e);
      toast.error('Failed to save adjuster details');
    }
  };

  const handleGenerateLor = async () => {
    try {
      const tpl = displayTemplates.find((t) => t.id === selectedTemplateId);
      if (!tpl) {
        toast.error('No LoR template available for this coverage');
        return;
      }
      const mergeFields = buildMergeFields();
      // Mirror coverage id inside merge_fields for backend audit context
      if (coverageId && !mergeFields.coverage_id) {
        mergeFields.coverage_id = coverageId;
      }
      const doc = await createDocument({
        matterId,
        data: {
          document_template_id: tpl.id,
          coverage_id: coverageId,
          merge_fields: mergeFields,
        },
      }).unwrap();

      // Update generated list
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // Immediately open preview for the newly created document
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setIsPreviewRequested(true);
      setPreviewOpen(true);
      setPreviewDocId(doc.id);
      requestPreview();
      toast.success('LoR generated');
      refetchDocs();
    } catch (e) {
      // eslint-disable-next-line no-console
      // eslint-disable-next-line no-console
      console.error('LoR generation failed', e);
      toast.error('Failed to generate LoR');
    }
  };

  const {
    url: previewUrlHook,
    filename: previewFilename,
    isLoading: isPreviewLoadingHook,
    error: previewError,
    request: requestPreview,
  } = useLorPreview(matterId, previewDocId);

  const backendHost =
    (
      import.meta as unknown as {
        env?: { VITE_APP_DJANGO_BACKEND_HOST?: string };
      }
    ).env?.VITE_APP_DJANGO_BACKEND_HOST || '';

  useEffect(() => {
    if (previewUrlHook) {
      setPreviewUrl(previewUrlHook);
      setPreviewOpen(true);
      setIsPreviewRequested(false);
    }
  }, [previewUrlHook]);

  useEffect(() => {
    if (previewError) {
      setIsPreviewRequested(false);
    }
  }, [previewError]);

  const isPreviewLoading =
    isPreviewLoadingHook ||
    (isPreviewRequested && !previewError && !previewUrl);

  // Build search terms for a given hover key; used by stacked transcript pane
  const getSearchTerms = (key: string): string[] => {
    const terms: string[] = [];
    const pushIfValid = (val: unknown) => {
      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed && trimmed.length >= 3) terms.push(trimmed);
        return;
      }
      if (
        val &&
        typeof val === 'object' &&
        Object.prototype.hasOwnProperty.call(
          val as Record<string, unknown>,
          'value',
        )
      ) {
        const extracted = String(
          (val as Record<string, unknown>).value ?? '',
        ).trim();
        if (extracted && extracted.length >= 3) terms.push(extracted);
      }
    };
    if (key === 'final:policy') {
      pushIfValid(finalizeDetails.policyNumber);
      pushIfValid(fallbackPolicy);
    } else if (key === 'final:claim') {
      pushIfValid(finalizeDetails.claimNumber);
      pushIfValid(fallbackClaim);
    } else if (key === 'final:adjName') {
      pushIfValid(finalizeDetails.adjusterName);
      pushIfValid(fallbackAdjName);
    } else if (key === 'final:adjEmail') {
      pushIfValid(finalizeDetails.adjusterEmail);
      pushIfValid(fallbackEmail);
    } else if (key === 'final:adjFax') {
      pushIfValid(finalizeDetails.adjusterFax);
      pushIfValid(fallbackFax);
    } else if (key.startsWith('lor:')) {
      const mapping = key.slice(4);
      const edited = lorFieldEdits[mapping];
      if (edited) pushIfValid(edited);
      const dv = (requiredFieldValues as Record<string, unknown> | undefined)?.[
        mapping
      ];
      pushIfValid(dv);
    }
    return terms;
  };

  // Evidence keys to compute across transcripts: include finalize keys and LOR field keys
  const lorEvidenceKeys = useMemo(() => {
    const fields = (templateFields?.fields || []) as LorDisplayField[];
    return fields.map((f) => `lor:${f.reference}`);
  }, [templateFields]);
  const transcriptEvidenceKeys = useMemo(
    () => [
      'final:policy',
      'final:claim',
      'final:adjName',
      'final:adjEmail',
      'final:adjFax',
      ...lorEvidenceKeys,
    ],
    [lorEvidenceKeys],
  );

  const handleFieldHover = (key: string | null) => {
    setActiveFieldKey(key);
    setHoverKey(key);
  };

  // Debounce hover key at the source to smooth all downstream reactions
  // (pane switching, transcript focusing, and artifact reference term)
  // Import placed at file top: `import { useDebouncedValue } from '@/hooks/useDebouncedValue';`
  const debouncedHoverKey = useDebouncedValue(hoverKey, 180);

  // Determine whether transcript pane should be shown based on debounced hover and evidence
  const activeKey = debouncedHoverKey;
  const shouldShowTranscript = Boolean(
    hasBlandConversations && activeKey && evidenceByKey[activeKey || ''],
  );

  // Compute reference search term for the Artifact panel from the debounced hover key
  const referenceTerm = (() => {
    const terms = activeKey ? getSearchTerms(activeKey) : [];
    return terms.find((t) => t && t.trim().length >= 3) || '';
  })();

  const renderRightPane = () => {
    if (isConvosLoading) {
      return (
        <div className="col-span-7 h-full flex items-center justify-center text-slate-500 text-sm">
          Loading conversation…
        </div>
      );
    }

    return (
      <div className="col-span-8 border rounded bg-white overflow-hidden h-full flex flex-col">
        <div className="p-3 border-b">
          <div className="text-sm font-semibold">
            {shouldShowTranscript
              ? 'Transcript & Audio'
              : 'Evidence & Documents'}
          </div>
          <div className="text-xs text-gray-500">
            Hover a field on the left to jump here
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden relative">
          {/* Transcript Pane - kept mounted, visibility toggled */}
          <div
            className={shouldShowTranscript ? 'block h-full' : 'hidden h-full'}
            aria-hidden={!shouldShowTranscript}
          >
            <StackedTranscriptPane
              matterId={matterId}
              conversations={
                (conversations as unknown as Array<{
                  id: string;
                  name?: string;
                  start_timestamp?: string;
                  created_at?: string;
                  source?: string;
                  recording_url?: string | null;
                }>) || []
              }
              activeHoverKey={debouncedHoverKey}
              getSearchTerms={getSearchTerms}
              onEvidenceComputed={(map) => setEvidenceByKey(map)}
              evidenceKeys={transcriptEvidenceKeys}
            />
          </div>

          {/* Artifact Browser Pane - kept mounted, visibility toggled */}
          <div
            className={
              !shouldShowTranscript
                ? 'block h-full overflow-auto p-3'
                : 'hidden h-full overflow-auto p-3'
            }
            aria-hidden={shouldShowTranscript}
          >
            <ArtifactBrowserPanel
              matterId={matterId}
              referenceSearchTerm={referenceTerm}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto w-full max-w-[2200px] px-8 py-4 space-y-4">
      <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)]">
        <div className="col-span-4 overflow-hidden h-full">
          <div className="p-3 h-full overflow-y-auto space-y-4">
            {!(
              coverage?.carrier?.handles && coverage.carrier.handles.length > 0
            ) && (
              <div className="rounded border border-red-200 bg-red-50 p-2">
                <div className="text-xs font-semibold text-red-800">
                  Missing carrier contact
                </div>
                <div className="text-[11px] text-red-700 mt-1">
                  <p>
                    Attach a carrier contact handle (phone, email, fax, website)
                    to this carrier.
                  </p>
                  <p>
                    Use the Carrier Directory to select the correct division and
                    handles.
                  </p>
                </div>
              </div>
            )}
            <div className="mb-2 flex items-center gap-3 items-center">
              <div className="w-3/4 min-w-0">
                <LorTemplateSelector
                  templates={displayTemplates}
                  selectedTemplateId={selectedTemplateId}
                  isLoading={isTemplatesLoading}
                  onChange={(v) => setSelectedTemplateId(v)}
                  filterActive={attorneyFilterActive}
                  filterLabel={representingAttorneyName ?? undefined}
                />
              </div>
              {displayTemplates.length > 0 && (
                <div className="w-1/4 mt-5">
                  <Button
                    className="w-full"
                    onClick={handleGenerateLor}
                    disabled={(() => {
                      if (isTemplatesLoading) return true;
                      if (!selectedTemplateId) return true;
                      if (isCreating) return true;
                      const fields = (templateFields?.fields ||
                        []) as LorDisplayField[];
                      if (!fields.length) return false;
                      const required = fields.filter((f) =>
                        Boolean(f.required),
                      );
                      if (required.length === 0) return false;
                      const currentValues = requiredFieldValues as Record<
                        string,
                        LorFieldValue
                      >;
                      const hasMissing = required.some((f) => {
                        const key = (f.reference || f.id || '').trim();
                        const v = currentValues[key];
                        if (typeof v === 'string') {
                          return v.trim() === '';
                        }
                        return true;
                      });
                      return hasMissing;
                    })()}
                    aria-busy={isCreating}
                  >
                    {isCreating ? 'Generating…' : 'Generate LoR'}
                  </Button>
                </div>
              )}
            </div>
            <DisclosureSection
              title="Review Call Output"
              isOpen={isReviewOutputOpen}
              onToggle={() => setIsReviewOutputOpen((v) => !v)}
            >
              <FinalizeDetailsPanel
                values={{
                  policyNumber: finalizeDetails.policyNumber || fallbackPolicy,
                  claimNumber: finalizeDetails.claimNumber || fallbackClaim,
                  adjusterName: finalizeDetails.adjusterName || fallbackAdjName,
                  adjusterEmail: finalizeDetails.adjusterEmail || fallbackEmail,
                  adjusterFax: finalizeDetails.adjusterFax || fallbackFax,
                  adjusterPhone: finalizeDetails.adjusterPhone || '',
                }}
                hasUnsavedChanges={hasUnsavedFinalizeChanges}
                noTemplatesAvailable={
                  !isTemplatesLoading && displayTemplates.length === 0
                }
                evidenceByKey={evidenceByKey}
                onHover={handleFieldHover}
                onChange={(field, value) =>
                  updateFinalizeDetail(
                    field as keyof typeof finalizeDetails,
                    value,
                  )
                }
                onSave={{
                  savePolicy: () =>
                    persistPolicyNumber(finalizeDetails.policyNumber || ''),
                  saveClaim: () =>
                    persistClaimNumber(finalizeDetails.claimNumber || ''),
                  saveAdjName: () =>
                    persistAdjuster({
                      adjusterName: finalizeDetails.adjusterName,
                    }),
                  saveAdjEmail: () =>
                    persistAdjuster({
                      adjusterEmail: finalizeDetails.adjusterEmail,
                    }),
                  saveAdjFax: () =>
                    persistAdjuster({
                      adjusterFax: finalizeDetails.adjusterFax,
                    }),
                  saveAdjPhone: () =>
                    persistAdjuster({
                      adjusterPhone: finalizeDetails.adjusterPhone,
                    }),
                }}
                onCancel={{
                  cancelPolicy: () =>
                    updateFinalizeDetail(
                      'policyNumber',
                      finalizeDetails.policyNumber,
                    ),
                  cancelClaim: () =>
                    updateFinalizeDetail(
                      'claimNumber',
                      finalizeDetails.claimNumber,
                    ),
                  cancelAdjName: () =>
                    updateFinalizeDetail(
                      'adjusterName',
                      finalizeDetails.adjusterName,
                    ),
                  cancelAdjEmail: () =>
                    updateFinalizeDetail(
                      'adjusterEmail',
                      finalizeDetails.adjusterEmail,
                    ),
                  cancelAdjFax: () =>
                    updateFinalizeDetail(
                      'adjusterFax',
                      finalizeDetails.adjusterFax,
                    ),
                  cancelAdjPhone: () =>
                    updateFinalizeDetail(
                      'adjusterPhone',
                      finalizeDetails.adjusterPhone,
                    ),
                }}
                onGenerate={handleGenerateLor}
                isGenerateDisabled={
                  isTemplatesLoading ||
                  !selectedTemplateId ||
                  isCreating ||
                  displayTemplates.length === 0
                }
                isGenerating={isCreating}
              />
            </DisclosureSection>
            <DisclosureSection
              title="LOR Fields"
              isOpen={isRequiredFieldsOpen}
              onToggle={() => setIsRequiredFieldsOpen((v) => !v)}
            >
              <RequiredLorFieldsPanel
                fields={
                  (templateFields?.fields || []) as LorDisplayField[]
                }
                values={requiredFieldValues}
                isLoading={isTemplateFieldsLoading}
                onHover={handleFieldHover}
                onDraftChange={(mapping, value) => {
                  // Capture in-progress edits so Generate uses the freshest values
                  const fieldValue = value as LorFieldValue;

                  // Auto-infer contact method when adjuster contact changes
                  if (mapping === DocumentFieldMapping.ADJUSTER_CONTACT) {
                    let method: string | undefined;

                    if (isScalarChoiceValue(fieldValue)) {
                      // Use the handle_type directly from structured data
                      method = fieldValue.context?.contact_handle?.handle_type;
                    }

                    setLorFieldEdits((prev) => ({
                      ...prev,
                      [mapping]: fieldValue,
                      ...(method && {
                        [DocumentFieldMapping.CONTACT_METHOD]: method,
                      }),
                    }));
                  } else {
                    setLorFieldEdits((prev) => ({
                      ...prev,
                      [mapping]: fieldValue,
                    }));
                  }
                }}
                onChange={async (mapping, value) => {
                  const fieldValue = value as LorFieldValue;
                  setLorFieldEdits((prev) => ({
                    ...prev,
                    [mapping]: fieldValue,
                  }));
                  // Persist to Coverage for specific fields when possible
                  try {
                    if (mapping === DocumentFieldMapping.POLICY_NUMBER) {
                      await persistPolicyNumber(toDisplayString(fieldValue));
                    } else if (mapping === DocumentFieldMapping.CLAIM_NUMBER) {
                      await persistClaimNumber(toDisplayString(fieldValue));
                    } else if (mapping === DocumentFieldMapping.ADJUSTER_NAME) {
                      await persistAdjuster({
                        adjusterName: toDisplayString(fieldValue),
                      });
                    } else if (
                      mapping === DocumentFieldMapping.ADJUSTER_CONTACT ||
                      mapping === DocumentFieldMapping.CONTACT_METHOD
                    ) {
                      let method: string | undefined;
                      let handleValue: string | undefined;
                      let choice: LoRScalarChoice | null = null;

                      if (mapping === DocumentFieldMapping.ADJUSTER_CONTACT) {
                        if (isScalarChoiceValue(fieldValue)) {
                          choice = fieldValue;
                          handleValue =
                            fieldValue.context?.contact_handle?.value ||
                            fieldValue.value;
                          method =
                            fieldValue.context?.contact_handle?.handle_type;
                        }
                      } else if (typeof fieldValue === 'string') {
                        method = fieldValue.trim().toUpperCase() || undefined;
                      }

                      const override: Partial<typeof finalizeDetails> = {};

                      if (choice) {
                        override.adjusterContactChoice = choice;
                        updateFinalizeDetail('adjusterContactChoice', choice);
                      } else if (
                        mapping === DocumentFieldMapping.ADJUSTER_CONTACT
                      ) {
                        updateFinalizeDetail('adjusterContactChoice', null);
                      }

                      if (handleValue) {
                        if (method === 'FAX') {
                          override.adjusterFax = handleValue;
                          updateFinalizeDetail('adjusterFax', handleValue);
                        } else if (method === 'EMAIL') {
                          override.adjusterEmail = handleValue;
                          updateFinalizeDetail('adjusterEmail', handleValue);
                        } else if (method === 'PHONE') {
                          override.adjusterPhone = handleValue;
                          updateFinalizeDetail('adjusterPhone', handleValue);
                        } else {
                          // If we don't have a method, we shouldn't try to persist the contact
                          // eslint-disable-next-line no-console
                          console.warn(
                            'Contact method not determined for value:',
                            handleValue,
                          );
                        }
                      }

                      // Automatically set the contact method when adjuster contact changes
                      if (
                        mapping === DocumentFieldMapping.ADJUSTER_CONTACT &&
                        method
                      ) {
                        setLorFieldEdits((prev) => ({
                          ...prev,
                          [DocumentFieldMapping.CONTACT_METHOD]: method,
                        }));
                      }

                      if (Object.keys(override).length > 0) {
                        await persistAdjuster(override);
                      }
                    } else if (
                      mapping === DocumentFieldMapping.CARRIER_ADDRESS_1
                    ) {
                      // Persist only the first line as the canonical carrier address
                      await updateCoverage({
                        matterId,
                        coverageId,
                        coverage: {
                          carrier: {
                            name: coverage?.carrier?.name ?? null,
                            handles: coverage?.carrier?.handles ?? null,
                            address: toDisplayString(fieldValue),
                          },
                        },
                      }).unwrap();
                      saveFinalizeDetails();
                    } else if (
                      mapping === DocumentFieldMapping.CARRIER_ADDRESS_2
                    ) {
                      // Do not persist line 2; only use for document merge fields
                    } else if (mapping === DocumentFieldMapping.CARRIER_NAME) {
                      await updateCoverage({
                        matterId,
                        coverageId,
                        coverage: {
                          carrier: {
                            name: toDisplayString(fieldValue) || null,
                            handles: coverage?.carrier?.handles ?? null,
                            address: coverage?.carrier?.address ?? null,
                          },
                        },
                      }).unwrap();
                      saveFinalizeDetails();
                    }
                    // Fire LOR_DATA_UPDATED event after any field change (best-effort)
                    try {
                      await lorDataUpdated({
                        matterId,
                        coverageId,
                        body: {
                          document_id: previewDocId || null,
                          mapping,
                          old_value: undefined, // not tracked client-side per-field
                          new_value:
                            typeof fieldValue === 'string'
                              ? fieldValue
                              : ((fieldValue as LoRScalarChoice | undefined)
                                  ?.value ?? null),
                        },
                      }).unwrap();
                    } catch (e) {
                      // silenced by design; this is an analytics/audit signal
                    }
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    // eslint-disable-next-line no-console
                    console.error('Failed to persist LoR required field', err);
                  }
                }}
              />
            </DisclosureSection>
          </div>
        </div>

        {renderRightPane()}
      </div>

      {isTemplateArchived && (
        <ArchivedTemplateWarning
          templateStatus={templateStatus}
          templateName={archivedTemplateName}
          isError={isArchiveCheckError}
          onSelectNewTemplate={() => {
            setSelectedTemplateId(null);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            toast.info(
              'Please select an active template above and regenerate the LOR.',
            );
          }}
        />
      )}

      <div className="mb-2 flex justify-end gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setIsEmailDialogOpen(true)}
          disabled={isTemplateArchived || isArchiveCheckLoading}
        >
          Email LOR
        </Button>
        <Button
          size="sm"
          variant="default"
          onClick={() => setIsFaxWizardOpen(true)}
          disabled={isTemplateArchived || isArchiveCheckLoading}
        >
          Send LOR via Fax
        </Button>
      </div>
      <LorList
        matterId={matterId}
        docs={lorDocs}
        isLoading={isDocsLoading}
        docsError={docsError}
        templatesError={templatesError}
        backendHost={backendHost}
        coveragesMap={coveragesMap}
        currentCoverageId={coverageId}
        onView={(id: string) => handleViewLor(id)}
        onLoad={(id: string) => handleViewLor(id)}
      />
      <Dialog open={isFaxWizardOpen} onOpenChange={setIsFaxWizardOpen}>
        <RightDialogContent className="bg-white h-[95vh] overflow-hidden flex flex-col">
          <DialogTitle className="sr-only">Send Fax</DialogTitle>
          <div className="flex-1 overflow-auto">
            <FaxWizard
              matterId={matterId}
              initialData={{
                recipient_number:
                  finalizeDetails.adjusterFax ||
                  fallbackFax ||
                  fallbackCarrierFax ||
                  '',
                attachment_blob_ids: [],
                attachment_document_ids: lorDocsForCoverage.map((d) => d.id),
              }}
              documentItems={lorDocsForCoverage.map((d) => ({
                id: d.id,
                title: d.document_template?.name || 'Letter of Representation',
                createdAt: d.created_at,
              }))}
              onSuccess={async () => {
                setIsFaxWizardOpen(false);
                toast.success('Fax flow completed');
                await markSendLorTaskCompleted();
              }}
              onCancel={() => setIsFaxWizardOpen(false)}
            />
          </div>
        </RightDialogContent>
      </Dialog>
      <LorPreviewDialog
        open={previewOpen}
        onOpenChange={handlePreviewOpenChange}
        isLoading={isPreviewLoading}
        previewUrl={previewUrl}
        error={previewError}
        onDownloadDocx={handleDownloadDocx}
        onDownloadPdf={handleDownloadPdf}
        disableActions={isPreviewLoading}
        appendOptions={appendPdfOptions}
        selectedAppendId={selectedAppendBlobId}
        onChangeAppendId={(id) => setSelectedAppendBlobId(id)}
      />

      <SendFrontEmailDialog
        matterId={matterId}
        open={isEmailDialogOpen}
        onClose={() => setIsEmailDialogOpen(false)}
        defaultTo={buildDefaultEmailTo()}
        defaultSubject={buildDefaultEmailSubject()}
        defaultBody=""
        headerContent={{
          title: 'Send Letter of Representation',
          subtitle: 'Email insurer via Front',
        }}
      />
    </div>
  );
};
